import { sleep } from '@cpmech/basic';

// reference:
// https://github.com/guilhermehn/gerar-cpf/blob/master/index.js
const mod = (x, y) => Math.round(x - Math.floor(x / y) * y);
const sumNumbers = (xs) => xs
    .slice()
    .reverse()
    .reduce((acc, a, b) => acc + a * (b + 2), 0);
const genCPF = (rng = Math.random, mask = 'xxx.xxx.xxx-xx') => {
    const numbers = [];
    while (numbers.length < 9) {
        numbers[numbers.length] = Math.round(rng() * 9);
    }
    while (numbers.length < 11) {
        let last = 11 - mod(sumNumbers(numbers), 11);
        if (last > 9) {
            last = 0;
        }
        numbers[numbers.length] = last;
    }
    const result = numbers.join('');
    if (!mask) {
        return result;
    }
    const placeholder = 'x';
    const placeholderRegex = new RegExp(placeholder);
    for (let i = 0; i < 11; i++) {
        mask = mask.replace(placeholderRegex, result[i]);
    }
    return mask;
};
const reduceNumbersForCPF = (numbers, quantity) => numbers.slice(0, quantity).reduce((acc, curr, i) => acc + curr * (quantity + 1 - i), 0);
const calculateCheckDigit = (reducedNumbers) => ((reducedNumbers * 10) % 11) % 10;
const isValidCPF = (originalValue) => {
    const value = originalValue.replace(/[\D\s._\-]+/g, '');
    if (!value || value.length !== 11) {
        return false;
    }
    const asNumbers = value.split('').map(n => Number(n));
    // not repeated
    const set = new Set(asNumbers);
    if (set.size === 1) {
        return false;
    }
    const check1 = reduceNumbersForCPF(asNumbers, 9);
    if (calculateCheckDigit(check1) !== asNumbers[9]) {
        return false;
    }
    const check2 = reduceNumbersForCPF(asNumbers, 10);
    if (calculateCheckDigit(check2) !== asNumbers[10]) {
        return false;
    }
    return true;
};
const formatCPF = (input) => {
    const value = input.replace(/[\D\s._-]+/g, '');
    let split = 4;
    const chunk = [];
    for (let i = 0, len = value.length; i < len; i += split) {
        split = i < 9 ? 3 : 2;
        chunk.push(value.substr(i, split));
    }
    if (chunk.length >= 4) {
        return chunk.slice(0, 3).join('.') + '-' + chunk[3];
    }
    return chunk.join('.');
};

const isValidPositiveNumber = (x) => {
    const val = Number(x);
    if (isNaN(val) || !isFinite(val)) {
        return false;
    }
    if (val <= 0) {
        return false;
    }
    return true;
};

const dateTranslationEn = {
    year: 'Year',
    month: 'Month',
    day: 'Day',
    date: 'Date',
    errorYear: 'Please enter year (4 digits)',
    errorMonth: 'Please enter month',
    errorDay: 'Please enter day',
    errorDate: 'Date {{date}} is invalid',
};
const dateTranslationPt = {
    year: 'Ano',
    month: 'Mês',
    day: 'Dia',
    date: 'Data',
    errorYear: 'Por favor entrar o ano (4 dígitos)',
    errorMonth: 'Por favor entrar o mês',
    errorDay: 'Por favor entrar o dia',
    errorDate: 'A data {{date}} é inválida',
};
// returns empty on errors
const date2values = (dateISO) => {
    const date = dateISO.split('T')[0];
    if (!date) {
        return { year: '', month: '', day: '' };
    }
    const [year, month, day] = date.split('-');
    if (!year ||
        !month ||
        !day ||
        !isValidPositiveNumber(year) ||
        !isValidPositiveNumber(month) ||
        !isValidPositiveNumber(day)) {
        return { year: '', month: '', day: '' };
    }
    const d = new Date(date); // must not use dateISO, otherwise the timezone will kick in
    if (d.getFullYear() !== Number(year) ||
        d.getMonth() + 1 !== Number(month) ||
        d.getDate() !== Number(day)) {
        return { year: '', month: '', day: '' };
    }
    return { year, month, day };
};
// returns empty on errors
const values2date = (values) => {
    const month = values.month.padStart(2, '0');
    const day = values.day.padStart(2, '0');
    const date = `${values.year}-${month}-${day}`;
    const d = new Date(date);
    const ok = d.getFullYear() === Number(values.year) &&
        d.getMonth() + 1 === Number(values.month) &&
        d.getDate() === Number(values.day);
    if (!ok) {
        return '';
    }
    return date;
};
const values2errors = (values, translation = dateTranslationEn) => {
    const errors = {
        year: values.year && values.year.length === 4 ? '' : translation.errorYear,
        month: values.month && values.month !== '0' ? '' : translation.errorMonth,
        day: values.day && values.day !== '0' ? '' : translation.errorDay,
        date: '',
    };
    if (errors.year || errors.month || errors.day) {
        return { errors, date: '' };
    }
    const date = values2date(values);
    if (!date) {
        errors.date = translation.errorDate.replace('{{date}}', `${values.year}-${values.month.padStart(2, '0')}-${values.day.padStart(2, '0')}`);
    }
    return { errors, date };
};

// exponential backoff (with jitter)
// NOTE: the base component of backoff is fixed to ensure minimum total wait time on slow targets.
// Returns success flag
// REFS:
//  [1] https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
//  [2] https://github.com/aws/aws-cdk/blob/db77bfecb98ffdcb092cf74bc7d79e6880a73b69/packages/%40aws-cdk/aws-certificatemanager/lambda-packages/dns_validated_certificate_handler/lib/index.js#L103
const exponentialBackoff = async (action, maxAttempts = 10, maxMilliseconds = 3600000, // 1 min
withJitter = true, baseFixedMS = 150, // milliseconds
baseMultMS = 50) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const success = await action();
        if (success) {
            return true; // success
        }
        const x = Math.pow(2, attempt);
        let dt = withJitter
            ? (baseFixedMS + baseMultMS * Math.random()) * x
            : (baseFixedMS + baseMultMS) * x;
        if (dt > maxMilliseconds) {
            dt = maxMilliseconds;
        }
        await sleep(dt);
    }
    return false; // fail
};

const fileExt2contentType = {
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf',
    txt: 'text/plain',
    xml: 'text/xml',
    json: 'application/json',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};
const name2fileExt = (filename) => {
    const array = filename.split('.');
    if (array.length < 2) {
        throw new Error('filename does not have extension');
    }
    const ext = array.pop();
    if (!ext) {
        throw new Error('filename does not have extension');
    }
    const e = ext.trim();
    if (!e) {
        throw new Error('filename does not have extension');
    }
    const res = e.toLowerCase();
    if (!fileExt2contentType[res]) {
        throw new Error(`file extension (${res}) is invalid`);
    }
    return res;
};

// formatLongNumber formats number 1000000 to 1,234,567
const formatLongNumber = (n, comma = ',') => {
    return n.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, comma);
};
// formatValue appends prefix to value and fixes decimal side
// ref: https://codepen.io/559wade/pen/LRzEjj
const formatNumber = (inputVal, swapDotByComma = false, numDigits = 2, prefix = '') => {
    // don't validate empty input
    if (!inputVal) {
        return '';
    }
    // constants
    const point = swapDotByComma ? ',' : '.';
    const comma = swapDotByComma ? '.' : ',';
    // check for decimal
    if (inputVal.indexOf(point) >= 0) {
        // get position of last decimal. this prevents multiple decimals from being entered
        const decimalPos = inputVal.indexOf(point);
        // split number by decimal point
        let leftSide = inputVal.substring(0, decimalPos).replace(/\D/g, '');
        let rightSide = inputVal.substring(decimalPos).replace(/\D/g, '');
        // handle left = 0
        if (leftSide.length === 0) {
            leftSide += '0';
        }
        else {
            if (Number(leftSide) === 0) {
                leftSide = '0';
            }
        }
        // add commas to left side of number
        leftSide = formatLongNumber(leftSide, comma);
        // Limit decimal to only numDigits digits
        rightSide = rightSide.substring(0, numDigits);
        // join number by "point"
        inputVal = prefix + leftSide + point + rightSide;
    }
    else {
        // no decimal entered./ add commas to number and remove all non-digits
        inputVal = formatLongNumber(inputVal, comma);
        inputVal = prefix + inputVal;
    }
    // results
    return inputVal;
};
const cleanNumber = (formattedValue, swapDotByCommaInTheInput = false, // the output is always dot
prefix = '') => {
    // get inputVal
    let inputVal = formattedValue.replace(prefix, '').trim();
    // skip empty input
    if (!inputVal) {
        return '';
    }
    // constants
    const point = swapDotByCommaInTheInput ? ',' : '.';
    // check for decimal
    if (inputVal.indexOf(point) >= 0) {
        // get position of last decimal
        const decimalPos = inputVal.indexOf(point);
        // split number by decimal point
        const leftSide = inputVal.substring(0, decimalPos).replace(/\D/g, '');
        const rightSide = inputVal.substring(decimalPos).replace(/\D/g, '');
        // join number by "."
        inputVal = leftSide + '.' + rightSide;
    }
    else {
        inputVal = inputVal.replace(/\D/g, '');
    }
    // results
    return inputVal;
};

// Ref: http://form.guide/best-practices/validate-email-address-using-javascript.html
// eslint-disable-next-line
const re$1 = /^(?:[a-z0-9!#$%&amp;'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&amp;'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;
const isEmailValid = (email) => re$1.test(String(email).toLowerCase());

// tslint:disable-next-line: max-line-length
// To check a password between 8 to 15 characters which contain at least one lowercase letter, one uppercase letter, one numeric digit, and one special character
// ref: https://www.w3resource.com/javascript/form/password-validation.php
const re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/;
const isPasswordValid = (pwd) => re.test(pwd);

const isValidNumber = (x) => {
    if (x === '') {
        return false;
    }
    const val = Number(x);
    if (isNaN(val) || !isFinite(val)) {
        return false;
    }
    return true;
};

// oKeys returns a typed array with the keys of an object
const objKeys = (obj) => Object.keys(obj);

// removes entries with null values
const noNull = (obj) => {
    return objKeys(obj).reduce((acc, key) => {
        if (obj[key] === null) {
            return acc;
        }
        return {
            ...acc,
            [key]: obj[key],
        };
    }, {});
};

// removes entries with undefined values
const noUndef = (obj) => {
    return objKeys(obj).reduce((acc, key) => {
        if (obj[key] === undefined) {
            return acc;
        }
        return {
            ...acc,
            [key]: obj[key],
        };
    }, {});
};

export { cleanNumber, date2values, dateTranslationEn, dateTranslationPt, exponentialBackoff, fileExt2contentType, formatCPF, formatLongNumber, formatNumber, genCPF, isEmailValid, isPasswordValid, isValidCPF, isValidNumber, isValidPositiveNumber, name2fileExt, noNull, noUndef, objKeys, values2date, values2errors };
